import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Container, Typography, TextField, CircularProgress, Paper, Chip, Button,
  Snackbar, Alert, InputAdornment, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, Grid, Divider, IconButton, Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import HistoryIcon from '@mui/icons-material/History';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import PurchaseItemsModal from '../../cardManagement/components/PurchaseItemsModal';
import { useAuth } from '../../../contexts/AuthContext';
import { useParams } from 'react-router-dom';
import { cardManagementService } from '../../../services/cardManagementService';
import salesService from '../../../services/salesService';
import { setLocalServiceUrl as setPavoPayLocalServiceUrl, processAdvanceSale, getReturnableSale, processPartialReturnForAdvanceSale, PartialReturnPayment } from './hooks/fiscalDevices/pavopay';
import { setLocalServiceUrl as setIngenicoLocalServiceUrl, ingenico5000fDevice, checkIngenicoOpenTransaction, cancelIngenicoOpenTransaction } from './hooks/fiscalDevices/ingenico';
import PaymentWaitingModal from './components/PaymentWaitingModal';
import PavoRestP2PWaitModal, { type RestP2PWaitStatus } from './components/modals/PavoRestP2PWaitModal';
import { setRestP2PConfig, clearRestP2PConfig } from '../../../services/restP2PConfig';
import type { RestP2PPaymentPart } from '../../../services/restP2PPaymentService';
import { getSellerByScreen, type SellerSnapshot } from '../../../services/sellerInfoService';
import { useTranslation } from 'react-i18next';

const formatCurrency = (amount: number) =>
  amount.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ₺';

// Ingenico POS bakiye yükleme başlarken kart/cüzdan bağlamını saklarız. Cihaz fişinde
// PYK kart bilgisi olmadığı için, timeout/sayfa yenileme sonrası yarım kalan işlemi
// DOĞRU karta işleyebilmek için bu bağlamı localStorage'da tutarız.
interface PendingIngenicoLoad {
  cardId: number;
  cardNumber?: string;
  walletId: number;
  amount: number;
  screenId?: string;
  createdAt: number;
}
const PYK_INGENICO_PENDING_KEY = 'pyk_ingenico_pending_load';
// 6 saatten eski kayıtlar bayat sayılır (eski/alakasız işlemleri yanlış karta yazmamak için).
const PENDING_MAX_AGE_MS = 6 * 60 * 60 * 1000;

const savePendingIngenicoLoad = (ctx: PendingIngenicoLoad) => {
  try { localStorage.setItem(PYK_INGENICO_PENDING_KEY, JSON.stringify(ctx)); } catch { /* yoksay */ }
};
const readPendingIngenicoLoad = (screenId?: string): PendingIngenicoLoad | null => {
  try {
    const raw = localStorage.getItem(PYK_INGENICO_PENDING_KEY);
    if (!raw) return null;
    const ctx = JSON.parse(raw) as PendingIngenicoLoad;
    if (!ctx || !ctx.cardId || !ctx.walletId) return null;
    if (Date.now() - (ctx.createdAt || 0) > PENDING_MAX_AGE_MS) return null;
    if (screenId && ctx.screenId && ctx.screenId !== screenId) return null;
    return ctx;
  } catch { return null; }
};
const clearPendingIngenicoLoad = () => {
  try { localStorage.removeItem(PYK_INGENICO_PENDING_KEY); } catch { /* yoksay */ }
};

const formatAmountInput = (value: string): string => {
  let cleaned = value.replace(/[^0-9,]/g, '');
  const parts = cleaned.split(',');
  if (parts.length > 2) cleaned = parts[0] + ',' + parts.slice(1).join('');
  const intPart = parts[0].replace(/^0+(?=\d)/, '');
  const decPart = parts[1]?.slice(0, 2);
  const formatted = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return decPart !== undefined ? `${formatted},${decPart}` : formatted;
};

const parseAmountInput = (value: string): number => {
  const cleaned = value.replace(/\./g, '').replace(',', '.');
  return parseFloat(cleaned) || 0;
};

const toLocalTime = (utcDate: string) => {
  const d = new Date(utcDate);
  return d.toLocaleString('tr-TR', { timeZone: 'Europe/Istanbul' });
};

const PykScreenView: React.FC = () => {
  const { t } = useTranslation('salesSystem');
  const { currentUser, userDetails } = useAuth();
  const { screenId } = useParams<{ screenId: string }>();
  const companyId = userDetails?.companyId || '';

  const [cardSearch, setCardSearch] = useState('');
  const [cardData, setCardData] = useState<any>(null);
  const [activeSession, setActiveSession] = useState<any>(null);
  const [walletBalances, setWalletBalances] = useState<any[]>([]);
  const [transactions, setTransactions] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Balance modals
  const [loadModalOpen, setLoadModalOpen] = useState(false);
  const [withdrawModalOpen, setWithdrawModalOpen] = useState(false);
  const [balanceAmount, setBalanceAmount] = useState('');
  const [balanceWalletId, setBalanceWalletId] = useState<number | ''>('');
  const [balanceDescription, setBalanceDescription] = useState('');
  const [balanceLoading, setBalanceLoading] = useState(false);

  // PYK screen payment settings
  const [paymentOptions, setPaymentOptions] = useState<any[]>([]);
  const cashOption = paymentOptions.find((p: any) => p.type === 'cash' && p.enabled);
  const posOption = paymentOptions.find((p: any) => p.type === 'pos' && p.enabled);

  // PavoPay yazarkasa
  const [usePavoPay, setUsePavoPay] = useState(false);

  // Ingenico yazarkasa (PYK bakiye işlemleri için)
  // Pavo'da avans çekiliyordu; Ingenico'da seçili ürün üzerinden gerçek satış kesilir.
  const [pykFiscalDevice, setPykFiscalDevice] = useState<'none' | 'pavopay' | 'ingenico_5000f'>('none');
  const [ingenicoBalanceProduct, setIngenicoBalanceProduct] = useState<{ id: string; name: string; vatRate: number; departmentNo: number } | null>(null);
  const useIngenico = pykFiscalDevice === 'ingenico_5000f';
  // Ekran açıldığında cihazda asılı kalan (önceki timeout'tan) yarım işlem tespiti.
  // tx: cihazdan okunan fiş durumu; ctx: yükleme başlarken kaydedilen kart bağlamı.
  const [ingenicoPending, setIngenicoPending] = useState<{
    tx: NonNullable<Awaited<ReturnType<typeof checkIngenicoOpenTransaction>>>;
    ctx: PendingIngenicoLoad | null;
  } | null>(null);
  const [ingenicoPendingLoading, setIngenicoPendingLoading] = useState(false);
  const [ingenicoPendingChecking, setIngenicoPendingChecking] = useState(false);
  // PYK ekranının bağlı olduğu şube — gün sonu/fatura kayıtları bu şubeyle yazılmalı
  // (kullanıcı şubesi farklı/boş olabilir; aksi halde gün sonu şube filtresinde görünmez).
  const [pykScreenBranchId, setPykScreenBranchId] = useState<string | null>(null);
  const [paymentWaitingOpen, setPaymentWaitingOpen] = useState(false);
  const [paymentWaitingStatus, setPaymentWaitingStatus] = useState<'waiting' | 'success' | 'error'>('waiting');
  const [paymentWaitingMessage, setPaymentWaitingMessage] = useState('');
  const [paymentWaitingMode, setPaymentWaitingMode] = useState<'payment' | 'refund'>('payment');

  // Rest-P2P (uzaktan PavoPay) — kapalıyken mevcut PaymentWaitingModal, aktifken PavoRestP2PWaitModal kullanılır
  const [useRestP2P, setUseRestP2P] = useState(false);
  const [restP2PDeviceCode, setRestP2PDeviceCode] = useState<string | null>(null);
  const [restP2PDeviceName, setRestP2PDeviceName] = useState<string | null>(null);
  const [restP2PModalOpen, setRestP2PModalOpen] = useState(false);
  const [restP2PStatus, setRestP2PStatus] = useState<RestP2PWaitStatus>('waiting');
  const [restP2PMessage, setRestP2PMessage] = useState<string | null>(null);
  const [restP2PMode, setRestP2PMode] = useState<'cash' | 'pos'>('pos');
  const [restP2PFlowMode, setRestP2PFlowMode] = useState<'payment' | 'refund'>('payment');
  const [restP2PTotal, setRestP2PTotal] = useState(0);
  const [restP2PParts, setRestP2PParts] = useState<RestP2PPaymentPart[]>([]);

  // Hybrid bekleme modalı yardımcıları — Rest-P2P aktifken PavoRestP2PWaitModal, kapalıyken PaymentWaitingModal
  const openWaitingModal = useCallback(
    (mode: 'payment' | 'refund', netMode: 'cash' | 'pos', amount: number) => {
      if (useRestP2P) {
        setRestP2PMode(netMode);
        setRestP2PFlowMode(mode);
        setRestP2PTotal(amount);
        setRestP2PParts([{ method: netMode, amount, order: 1 }]);
        setRestP2PStatus('waiting');
        setRestP2PMessage(null);
        setRestP2PModalOpen(true);
      } else {
        setPaymentWaitingMode(mode);
        setPaymentWaitingStatus('waiting');
        setPaymentWaitingMessage('');
        setPaymentWaitingOpen(true);
      }
    },
    [useRestP2P],
  );

  const setWaitingError = useCallback(
    (msg: string) => {
      if (useRestP2P) {
        setRestP2PStatus('error');
        setRestP2PMessage(msg || 'Pavo cihazı işlemi başarısız');
      } else {
        setPaymentWaitingStatus('error');
        setPaymentWaitingMessage(msg);
      }
    },
    [useRestP2P],
  );

  // Pavo cihazında "Vazgeç" basıldığında kullanılır. Rest-P2P modalı 'cancelled'
  // status'unu native olarak destekler ve flowMode'a göre uygun başlığı gösterir
  // (örn. POS iade akışında "POS İade İptal Edildi"). PaymentWaitingModal
  // (Rest-P2P kapalı) henüz cancelled state'i desteklemediği için 'error'a
  // fallback yaparız.
  const setWaitingCancelled = useCallback(
    (msg: string) => {
      if (useRestP2P) {
        setRestP2PStatus('cancelled');
        setRestP2PMessage(msg || 'Pavo cihazında iptal edildi');
      } else {
        setPaymentWaitingStatus('error');
        setPaymentWaitingMessage(msg || 'Pavo cihazında iptal edildi');
      }
    },
    [useRestP2P],
  );

  const setWaitingSuccess = useCallback(() => {
    if (useRestP2P) {
      setRestP2PStatus('success');
    } else {
      setPaymentWaitingStatus('success');
    }
  }, [useRestP2P]);

  const closeWaitingModal = useCallback(
    (delayMs = 1500) => {
      const close = () => {
        if (useRestP2P) setRestP2PModalOpen(false);
        else setPaymentWaitingOpen(false);
      };
      if (delayMs > 0) setTimeout(close, delayMs);
      else close();
    },
    [useRestP2P],
  );

  // History
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [sessionHistory, setSessionHistory] = useState<any[]>([]);

  // Purchase items modal
  const [purchaseItemsOpen, setPurchaseItemsOpen] = useState(false);
  const [purchaseItemsMode, setPurchaseItemsMode] = useState<'transaction' | 'session'>('transaction');
  const [purchaseReferenceId, setPurchaseReferenceId] = useState<string | null>(null);

  // NOT: PYK satış fişleme sistemi kaldırıldı. Ön ödemeli satışlar artık satış
  // ANINDA (backend PurchaseFromCardAsync içinde) gün sonu kaydı + fatura oluşturur;
  // burada Fiş Kes / toplu fişleme / fişlenmemiş satış kontrolleri yoktur.

  // POS Avans İade
  const [refundModalOpen, setRefundModalOpen] = useState(false);
  const [refundLoading, setRefundLoading] = useState(false);
  const [refundProcessing, setRefundProcessing] = useState(false);
  const [refundProgressMessage, setRefundProgressMessage] = useState('');
  const [posLoadTransactions, setPosLoadTransactions] = useState<any[]>([]);
  const [returnableSaleDataMap, setReturnableSaleDataMap] = useState<Record<number, any>>({});
  const [refundDistribution, setRefundDistribution] = useState<Array<{ txId: number; saleId: number; saleNumber?: string; saleUid?: string; paymentId: number; originalAmount: number; refundAmount: number; refundType: 'full' | 'partial'; mediatorId: number }>>([]);
  const [cashRefundAmount, setCashRefundAmount] = useState(0);
  const [posRefundTotal, setPosRefundTotal] = useState(0);
  // Kullanıcıya iade modalında "neden bu dağıtım" gerekçesini açıklayan özet.
  // Harcamalar önce POS yüklemelerinden eksiltilir; nakit yüklemenin
  // harcanmamış kısmı kesinlikle POS iadesine konu edilmez (kullanıcı şikayeti).
  const [refundBreakdown, setRefundBreakdown] = useState<{
    posLoaded: number;
    cashLoaded: number;
    totalSpent: number;
    posSpent: number;
    cashSpent: number;
    posRemaining: number;
    cashRemaining: number;
  } | null>(null);

  // Oturum Başlat/Kapat
  const [sessionStartLoading, setSessionStartLoading] = useState(false);
  const [sessionCloseLoading, setSessionCloseLoading] = useState(false);
  // Ekran ayarı: Oturum Başlat/Kapat butonlarını göster (varsayılan açık)
  const [showSessionButtons, setShowSessionButtons] = useState(true);

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'warning' }>({ open: false, message: '', severity: 'success' });

  // ✅ PR3 (Invoice Seller Snapshot Refactor): PYK ekranı için backend resolver'dan satıcı snapshot.
  // Frontend gösterim/log amaçlı; fatura keserken DTO'ya screenId+screenType eklediğimiz için backend
  // her durumda kendi resolve'unu yapar (defense-in-depth).
  const [seller, setSeller] = useState<SellerSnapshot | null>(null);

  const cardSearchRef = useRef<HTMLInputElement>(null);
  const amountInputRef = useRef<HTMLInputElement>(null);
  const withdrawAmountRef = useRef<HTMLInputElement>(null);

  const focusCardSearch = useCallback(() => {
    setTimeout(() => cardSearchRef.current?.focus(), 100);
  }, []);

  useEffect(() => { focusCardSearch(); }, [focusCardSearch]);

  useEffect(() => {
    if (cardData) return;
    const handleClick = () => {
      setTimeout(() => cardSearchRef.current?.focus(), 50);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [cardData]);

  useEffect(() => {
    if (!screenId) return;
    const loadScreenSettings = async () => {
      try {
        const screen = await salesService.getPykScreenById(Number(screenId));
        setPykScreenBranchId(screen?.BranchId || null);
        if (screen?.SettingsJson) {
          const settings = typeof screen.SettingsJson === 'string'
            ? JSON.parse(screen.SettingsJson)
            : screen.SettingsJson;
          setPaymentOptions(settings.paymentOptions || []);
          // Yazarkasa cihazı: yeni alan yoksa eski usePavoPay'den türet
          const resolvedDevice: 'none' | 'pavopay' | 'ingenico_5000f' =
            settings.pykFiscalDevice || (settings.usePavoPay ? 'pavopay' : 'none');
          setPykFiscalDevice(resolvedDevice);
          setUsePavoPay(resolvedDevice === 'pavopay');
          setIngenicoBalanceProduct(settings.ingenicoBalanceProduct || null);
          if (resolvedDevice === 'pavopay') {
            setPavoPayLocalServiceUrl('http://localhost:3003');
          }
          if (resolvedDevice === 'ingenico_5000f') {
            setIngenicoLocalServiceUrl('http://localhost:3003');
          }
          setUseRestP2P(Boolean(settings.useRestP2PForPayments));
          setRestP2PDeviceCode(settings.pavoRestP2PDeviceScreenCode || null);
          setRestP2PDeviceName(settings.pavoRestP2PDeviceName || null);
          // Oturum butonları: tanımlı değilse varsayılan AÇIK (true)
          setShowSessionButtons(settings.showSessionButtons !== false);
        }
      } catch (err) {
        console.error('[PYK] Ekran ayarları yüklenemedi:', err);
      }
    };
    loadScreenSettings();
  }, [screenId]);

  // Rest-P2P global config'i ayarlardan beslenir; kapalıysa temizlenir.
  // pavopay helper'ları (processAdvanceSale, processCashPayment, processPartialReturnForAdvanceSale)
  // bu config'i okuyup branch atar.
  useEffect(() => {
    const enabled = Boolean(useRestP2P && restP2PDeviceCode);
    if (enabled) {
      setRestP2PConfig({
        enabled: true,
        deviceCode: restP2PDeviceCode,
        companyId: companyId || null,
        branchId: null,
      });
    } else {
      clearRestP2PConfig();
    }
  }, [useRestP2P, restP2PDeviceCode, companyId]);

  // ✅ PR3 (Invoice Seller Snapshot Refactor): Session başında satıcı snapshot çek.
  // PykScreens tablosundan branch çözülür, Companies vs Branches.Firm* karar mantığı backend'de.
  useEffect(() => {
    if (!screenId) {
      setSeller(null);
      return;
    }
    const screenIdNum = Number(screenId);
    if (!Number.isFinite(screenIdNum) || screenIdNum <= 0) {
      setSeller(null);
      return;
    }
    let cancelled = false;
    getSellerByScreen(screenIdNum, 'Pyk')
      .then((snapshot) => {
        if (cancelled) return;
        setSeller(snapshot);
        console.log(
          '[PykScreenView] 🏪 Seller snapshot:',
          `${snapshot.sellerCompanyName} (${snapshot.source}, franchise=${snapshot.isFranchiseInvoice})`,
        );
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn('[PykScreenView] Seller fetch hatası:', err);
        setSeller(null);
      });
    return () => {
      cancelled = true;
    };
  }, [screenId]);

  const clearCard = () => {
    setCardData(null);
    setActiveSession(null);
    setWalletBalances([]);
    setTransactions([]);
    setCardSearch('');
    setError('');
    focusCardSearch();
  };

  const searchCard = async () => {
    if (!cardSearch.trim() || !companyId) return;
    setLoading(true);
    setError('');
    setCardData(null);
    setActiveSession(null);
    setWalletBalances([]);
    setTransactions([]);

    try {
      let res;
      if (cardSearch.length <= 20 && !cardSearch.includes(' ')) {
        try {
          res = await cardManagementService.getCardByUID(companyId, cardSearch.trim());
        } catch {
          res = null;
        }
      }

      if (!res || !res.data?.data) {
        const cardsRes = await cardManagementService.getCards(companyId, { search: cardSearch.trim(), pageSize: 1 });
        const cards = cardsRes.data?.data || [];
        if (cards.length > 0) {
          res = await cardManagementService.getCardById(cards[0].id, companyId);
        }
      }

      if (res?.data?.data) {
        const card = res.data.data;
        console.log('[KART] Kart bulundu:', JSON.stringify(card, null, 2));
        console.log('[KART] Kart wallets:', card.wallets);
        setCardData(card);
        await loadSessionInfo(card.id);
      } else {
        setError('Kart bulunamadı');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Kart aranırken hata oluştu');
    }
    setLoading(false);
  };

  const loadSessionInfo = async (cardId: number) => {
    try {
      console.log('[OTURUM] loadSessionInfo çağrıldı, cardId:', cardId);
      const sessionRes = await cardManagementService.getActiveSession(companyId, cardId);
      const session = sessionRes.data?.data;
      console.log('[OTURUM] Aktif oturum:', session);
      console.log('[OTURUM] walletBalances:', session?.walletBalances);
      setActiveSession(session || null);

      if (session) {
        setWalletBalances(session.walletBalances || []);
        const txRes = await cardManagementService.getSessionTransactions(session.id, companyId, 1, 20);
        setTransactions(txRes.data?.data || []);
      } else {
        setWalletBalances([]);
        setTransactions([]);
      }
    } catch (err) {
      console.error('[OTURUM] loadSessionInfo hatası:', err);
    }
  };

  // Bakiye yükleme/iade backend kaydı. Yazarkasa adımı tamamlandıktan (veya
  // belirsiz timeout sonrası kasiyer onayından) sonra çağrılır.
  const finalizeBalanceLoad = async (
    type: 'LOAD' | 'WITHDRAW',
    paymentMethod: 'CASH' | 'POS' | undefined,
    amount: number,
    fiscalInfo: { saleNumber?: string; orderNo?: string; invoiceNo?: string; fiscalTransactionData?: string; referenceId?: string; referenceType?: string } | null,
    opts?: { walletId?: number; description?: string; cardId?: number },
  ) => {
    const cardId = opts?.cardId ?? cardData?.id;
    if (!cardId) {
      setSnackbar({ open: true, message: 'Kart bulunamadı.', severity: 'error' });
      return;
    }
    const walletId = opts?.walletId ?? Number(balanceWalletId);
    if (!walletId) {
      setSnackbar({ open: true, message: 'Cüzdan bulunamadı.', severity: 'error' });
      return;
    }
    setBalanceLoading(true);
    try {
      const fn = type === 'LOAD' ? cardManagementService.loadBalance : cardManagementService.withdrawBalance;
      const payload: any = {
        CardId: cardId,
        WalletId: walletId,
        Amount: Math.round(amount * 100) / 100,
        Description: opts?.description || balanceDescription || undefined,
        CreatedBy: currentUser?.email || '',
        ScreenId: screenId || '',
      };

      if (paymentMethod) {
        payload.PaymentMethod = paymentMethod;
        if (paymentMethod === 'CASH' && cashOption) {
          payload.CashboxId = cashOption.cashboxId;
        } else if (paymentMethod === 'POS' && posOption) {
          payload.BankAccountId = posOption.bankAccountId;
          payload.BankSubAccountId = posOption.subAccountId;
        }
      }

      if (fiscalInfo) {
        payload.FiscalSaleNumber = fiscalInfo.saleNumber;
        payload.FiscalOrderNo = fiscalInfo.orderNo;
        payload.FiscalInvoiceNo = fiscalInfo.invoiceNo;
        payload.FiscalTransactionData = fiscalInfo.fiscalTransactionData;
        payload.ReferenceId = fiscalInfo.referenceId;
        payload.ReferenceType = fiscalInfo.referenceType;
      }

      console.log('[BAKİYE İŞLEM] API çağrısı:', type === 'LOAD' ? 'loadBalance' : 'withdrawBalance');
      console.log('[BAKİYE İŞLEM] PAYLOAD:', JSON.stringify(payload, null, 2));

      await fn(payload);
      console.log('[BAKİYE İŞLEM] ✅ API başarılı');
      clearPendingIngenicoLoad();

      setLoadModalOpen(false);
      setWithdrawModalOpen(false);
      setBalanceAmount('');
      setBalanceWalletId('');
      setBalanceDescription('');
      setSnackbar({ open: true, message: type === 'LOAD' ? 'Bakiye yüklendi' : 'Bakiye iade edildi', severity: 'success' });
      await loadSessionInfo(cardId);
      focusCardSearch();
    } catch (err: any) {
      console.error('[BAKİYE İŞLEM] ❌ API HATA:', err);
      console.error('[BAKİYE İŞLEM] ❌ response.data:', err.response?.data);
      console.error('[BAKİYE İŞLEM] ❌ response.status:', err.response?.status);
      console.error('[BAKİYE İŞLEM] ❌ message:', err.response?.data?.message);
      setSnackbar({ open: true, message: err.response?.data?.message || 'İşlem başarısız', severity: 'error' });
    }
    setBalanceLoading(false);
  };

  const handleBalanceOperation = async (type: 'LOAD' | 'WITHDRAW', paymentMethod?: 'CASH' | 'POS') => {
    console.log('[BAKİYE İŞLEM] ===== BAŞLANGIÇ =====');
    console.log('[BAKİYE İŞLEM] type:', type, 'paymentMethod:', paymentMethod);
    console.log('[BAKİYE İŞLEM] balanceWalletId:', balanceWalletId, 'balanceAmount (raw):', balanceAmount);
    console.log('[BAKİYE İŞLEM] cardData:', { id: cardData?.id, cardNumber: cardData?.cardNumber, cardUID: cardData?.cardUID });
    console.log('[BAKİYE İŞLEM] activeSession:', { id: activeSession?.id, status: activeSession?.status, accountName: activeSession?.accountName });
    console.log('[BAKİYE İŞLEM] walletBalances:', JSON.stringify(walletBalances));
    console.log('[BAKİYE İŞLEM] totalBalance (frontend):', totalBalance);
    console.log('[BAKİYE İŞLEM] cardWallets:', JSON.stringify(cardWallets));
    console.log('[BAKİYE İŞLEM] cashOption:', cashOption, 'posOption:', posOption);

    if (!balanceWalletId || !balanceAmount) {
      console.warn('[BAKİYE İŞLEM] Eksik alan: walletId=', balanceWalletId, 'amount=', balanceAmount);
      setSnackbar({ open: true, message: !balanceWalletId ? 'Cüzdan seçiniz' : 'Tutar giriniz', severity: 'warning' });
      return;
    }
    const amount = parseAmountInput(balanceAmount);
    console.log('[BAKİYE İŞLEM] parseAmountInput sonucu:', amount, 'Math.round:', Math.round(amount * 100) / 100);
    if (isNaN(amount) || amount <= 0) {
      setSnackbar({ open: true, message: 'Geçerli bir tutar giriniz', severity: 'error' });
      return;
    }

    setBalanceLoading(true);

    let fiscalInfo: { saleNumber?: string; orderNo?: string; invoiceNo?: string; fiscalTransactionData?: string; referenceId?: string; referenceType?: string } | null = null;

    // ── Ingenico POS bakiye yükleme: avans yerine seçili ürün üzerinden gerçek satış kesilir ──
    if (paymentMethod === 'POS' && useIngenico && type === 'LOAD') {
      if (!ingenicoBalanceProduct) {
        setSnackbar({ open: true, message: 'Ingenico için satılacak ürün ayarlardan seçilmemiş.', severity: 'error' });
        setBalanceLoading(false);
        return;
      }
      const saleAmount = Math.round(parseAmountInput(balanceAmount) * 100) / 100;
      const vatRate = ingenicoBalanceProduct.vatRate || 0;
      // FiscalProductItem.price KDV HARİÇ beklenir; cihaz katmanı KDV'yi ekler.
      // Fiş toplamı = yüklenen tutar olsun diye hariç fiyatı tutardan geri hesaplıyoruz.
      const priceExclVat = Math.round((saleAmount / (1 + vatRate / 100)) * 100) / 100;
      // Yarım kalma ihtimaline karşı kart/cüzdan bağlamını sakla (sayfa yenilense bile
      // doğru karta işlenebilsin diye). Çözüm sonunda (başarı/başarısız) temizlenir.
      savePendingIngenicoLoad({
        cardId: cardData.id,
        cardNumber: cardData.cardNumber || cardData.cardUID,
        walletId: Number(balanceWalletId),
        amount: saleAmount,
        screenId: screenId || undefined,
        createdAt: Date.now(),
      });
      openWaitingModal('payment', 'pos', saleAmount);
      setPaymentWaitingMessage('Ingenico POS satışı bekleniyor...');
      // 🔎 TANI: Ingenico'ya gönderilen departman. Ayardaki ürünün gerçek
      // departmanı taşınıyor mu, yoksa boş/0 olduğu için `|| 1` ile sabit 1'e
      // mi düşüyor? Konsoldan kontrol et: departmentNoRaw=undefined/0 ise ürünün
      // envanterdeki Departman No'su boş demektir (ayarda yeniden seç + kaydet).
      console.log('🔎 [PYK Ingenico] Satılacak ürün departmanı:', {
        productId: ingenicoBalanceProduct.id,
        productName: ingenicoBalanceProduct.name,
        departmentNoRaw: ingenicoBalanceProduct.departmentNo,
        departmentNoGonderilen: ingenicoBalanceProduct.departmentNo || 1,
        vatRate,
      });
      try {
        const ingResult = await ingenico5000fDevice.processPosPayment(
          [{
            id: ingenicoBalanceProduct.id,
            name: ingenicoBalanceProduct.name,
            quantity: 1,
            price: priceExclVat,
            vatRate,
            departmentNo: ingenicoBalanceProduct.departmentNo || 1,
          }],
          saleAmount,
        );
        if (!ingResult.success) {
          // Cihaz, GetTicket tabanlı paymentCompleted ile ödemeyi GERÇEKTE aldıysa
          // processPosPayment zaten success:true döner (ingenico.ts). Buraya düştüysek
          // ödeme alınmamış demektir → KASİYERE SORMADAN güvenli şekilde başarısız sayarız.
          // Ek güvenlik: cihazda hâlâ açık (yarım) bir işlem kaldıysa deterministik kontrol et;
          // ödeme tamamlanmışsa fişi kapatıp başarılı say, aksi halde iptal et.
          console.warn('[BAKİYE İŞLEM] Ingenico POS satışı başarısız döndü, cihaz durumu doğrulanıyor...', {
            errorCode: ingResult.errorCode,
            errorMessage: ingResult.errorMessage,
          });
          setPaymentWaitingMessage('Cihaz durumu doğrulanıyor...');

          let openTx: Awaited<ReturnType<typeof checkIngenicoOpenTransaction>> = null;
          try {
            openTx = await checkIngenicoOpenTransaction();
          } catch (verErr) {
            console.warn('[BAKİYE İŞLEM] Cihaz durumu sorgulanamadı:', verErr);
          }

          if (openTx && openTx.remainingAmountTL <= 0.01) {
            // Cihazda açık işlem var ve ödeme tamamlanmış → fişi kapat, başarılı say.
            console.log('[BAKİYE İŞLEM] Açık işlemde ödeme tamamlanmış (kalan ≈ 0), fiş kapatılıyor...');
            try { await cancelIngenicoOpenTransaction(); } catch (recErr) { console.warn('[BAKİYE İŞLEM] recover hatası:', recErr); }
            setWaitingSuccess();
            closeWaitingModal(1500);
            fiscalInfo = {
              fiscalTransactionData: JSON.stringify({ FiscalTransactionId: openTx.transactionHandle || null, recovered: true }),
              referenceId: openTx.transactionHandle,
              referenceType: 'INGENICO_SALE',
            };
            // Başarılı: aşağıdaki finalizeBalanceLoad ile bakiye yüklenir.
          } else {
            // Ödeme alınmamış (veya yarım) → varsa yarım işlemi iptal et, bakiye YÜKLEME.
            if (openTx) {
              console.warn('[BAKİYE İŞLEM] Açık işlemde ödeme tamamlanmamış (kalan:', openTx.remainingAmountTL, '), iptal ediliyor...');
              try { await cancelIngenicoOpenTransaction(); } catch (recErr) { console.warn('[BAKİYE İŞLEM] recover/void hatası:', recErr); }
            }
            clearPendingIngenicoLoad();
            setWaitingError(ingResult.errorMessage || 'Ödeme alınamadı; bakiye yüklenmedi.');
            closeWaitingModal(3000);
            setSnackbar({ open: true, message: ingResult.errorMessage || 'Ödeme alınamadı. Bakiye yüklenmedi.', severity: 'error' });
            setBalanceLoading(false);
            return;
          }
        } else {
          setWaitingSuccess();
          closeWaitingModal(1500);
          fiscalInfo = {
            fiscalTransactionData: JSON.stringify(ingResult.fiscalData || { FiscalTransactionId: ingResult.documentId || null }),
            referenceId: ingResult.documentId,
            referenceType: 'INGENICO_SALE',
          };
        }
      } catch (err: any) {
        setWaitingError(err.message || 'Ingenico bağlantı hatası');
        closeWaitingModal(2500);
        setSnackbar({ open: true, message: err.message || 'Ingenico bağlantı hatası', severity: 'error' });
        setBalanceLoading(false);
        return;
      }
    }

    if (paymentMethod === 'POS' && (usePavoPay || useRestP2P) && type === 'LOAD') {
      const advAmount = Math.round(parseAmountInput(balanceAmount) * 100) / 100;
      openWaitingModal('payment', 'pos', advAmount);
      try {
        const pavoResult = await processAdvanceSale(
          advAmount,
          balanceDescription || 'PYK Bakiye Yükleme',
          2
        );
        if (!pavoResult.success) {
          // Pavo cihazında "Vazgeç" basıldığında errorMessage 'Kullanıcı iptal etti'
          // ve cancelled=true gelir. Bunu hata yerine "iptal" olarak gösteririz.
          if (pavoResult.cancelled) {
            const cancelMsg = pavoResult.errorMessage || 'Bakiye yükleme iptal edildi';
            setWaitingCancelled(cancelMsg);
            closeWaitingModal(2500);
            setSnackbar({ open: true, message: cancelMsg, severity: 'warning' });
          } else {
            setWaitingError(pavoResult.errorMessage || 'POS işlemi başarısız');
            closeWaitingModal(2500);
            setSnackbar({ open: true, message: pavoResult.errorMessage || 'POS işlemi başarısız', severity: 'error' });
          }
          setBalanceLoading(false);
          return;
        }
        setWaitingSuccess();
        closeWaitingModal(1500);
        fiscalInfo = {
          saleNumber: pavoResult.saleNumber,
          orderNo: pavoResult.orderNo,
          invoiceNo: pavoResult.invoiceNo,
          fiscalTransactionData: pavoResult.fiscalTransactionData,
          referenceId: pavoResult.saleNumber,
          referenceType: 'PAVOPAY_ADVANCE',
        };
      } catch (err: any) {
        setWaitingError(err.message || 'PavoPay bağlantı hatası');
        closeWaitingModal(2500);
        setSnackbar({ open: true, message: err.message || 'PavoPay bağlantı hatası', severity: 'error' });
        setBalanceLoading(false);
        return;
      }
    }

    await finalizeBalanceLoad(type, paymentMethod, amount, fiscalInfo);
  };

  // Cihazda önceki bir timeout'tan kalan yarım işlem var mı kontrol et.
  // Manuel (butondan) veya otomatik (ekran/kart açılışında) çağrılır.
  const checkIngenicoPending = useCallback(async (manual = false) => {
    if (!useIngenico) {
      if (manual) setSnackbar({ open: true, message: 'Ingenico modu aktif değil.', severity: 'warning' });
      return;
    }
    if (manual) setIngenicoPendingChecking(true);
    try {
      const tx = await checkIngenicoOpenTransaction();
      if (tx) {
        // Yükleme başlarken kaydettiğimiz kart bağlamını eşleştir (hangi karta?).
        const ctx = readPendingIngenicoLoad(screenId || undefined);
        setIngenicoPending({ tx, ctx });
      } else if (manual) {
        setSnackbar({ open: true, message: 'Cihazda yarım kalmış işlem bulunamadı.', severity: 'success' });
      }
    } catch (e) {
      console.warn('[PYK] Ingenico yarım işlem kontrolü hatası:', e);
      if (manual) setSnackbar({ open: true, message: 'Cihaz durumu sorgulanamadı. Yerel servis/cihaz bağlantısını kontrol edin.', severity: 'error' });
    } finally {
      if (manual) setIngenicoPendingChecking(false);
    }
  }, [useIngenico]);

  // Ingenico modunda ekran açıldığında / kart okutulduğunda cihazda asılı kalmış
  // işlem var mı otomatik kontrol et; varsa kurtarma modalı açılır.
  useEffect(() => {
    if (useIngenico) {
      checkIngenicoPending(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useIngenico, cardData?.id]);

  // Yarım kalan işlemi kurtar: ödeme tamamlandıysa fişi kapatıp (istenirse) bakiye yükle;
  // tamamlanmadıysa işlemi iptal et.
  const handleIngenicoPendingRecover = async (alsoLoad: boolean) => {
    if (!ingenicoPending) return;
    const { tx, ctx } = ingenicoPending;
    const paymentComplete = tx.remainingAmountTL <= 0.01;
    setIngenicoPendingLoading(true);
    let recovered = false;
    try {
      recovered = (await cancelIngenicoOpenTransaction()).success;
    } catch (e) {
      console.warn('[PYK] Yarım işlem kurtarma hatası:', e);
    }

    if (alsoLoad && paymentComplete) {
      // Hangi karta? Önce yükleme başında kaydedilen bağlam (ctx), yoksa ekranda
      // okutulu kart. Bağlamdaki tutar fiş tutarıyla uyuşuyorsa onu kullan.
      const cardId = ctx?.cardId || cardData?.id;
      const walletId = ctx?.walletId || Number(balanceWalletId) || cardData?.wallets?.[0]?.walletId;
      const loadAmount = ctx?.amount || tx.totalAmountTL;
      if (!cardId || !walletId) {
        setSnackbar({ open: true, message: 'Bakiye yüklenecek kart/cüzdan bulunamadı. Lütfen kartı okutup tekrar deneyin.', severity: 'error' });
      } else {
        const fiscalInfo = {
          fiscalTransactionData: JSON.stringify({ FiscalTransactionId: tx.transactionHandle || null, recovered: true }),
          referenceId: tx.transactionHandle,
          referenceType: 'INGENICO_SALE',
        };
        await finalizeBalanceLoad('LOAD', 'POS', loadAmount, fiscalInfo, {
          cardId: Number(cardId),
          walletId: Number(walletId),
          description: 'Yarım kalan POS yükleme kurtarma',
        });
      }
    } else {
      setSnackbar({
        open: true,
        message: recovered
          ? (paymentComplete ? 'Fiş kapatıldı. Bakiye yüklenmedi.' : 'Yarım kalan işlem iptal edildi.')
          : 'Cihaz işlemi kapatılamadı, lütfen cihazı manuel kontrol edin.',
        severity: recovered ? 'success' : 'warning',
      });
    }
    clearPendingIngenicoLoad();
    setIngenicoPendingLoading(false);
    setIngenicoPending(null);
  };

  const loadHistory = async () => {
    if (!cardData) return;
    try {
      const res = await cardManagementService.getSessionHistory(companyId, cardData.id, 1, 20);
      setSessionHistory(res.data?.data || []);
      setHistoryModalOpen(true);
    } catch { }
  };

  const totalBalance = walletBalances.reduce((sum: number, w: any) => sum + (w.balance || 0), 0);

  const handleStartSession = async () => {
    if (!cardData) return;
    setSessionStartLoading(true);
    try {
      await cardManagementService.openSession({
        CardId: cardData.id,
        // Karta bağlı cari varsa oturuma taşı; yoksa generic (0).
        AccountId: (cardData.accountId && cardData.accountId.toString() !== '0') ? cardData.accountId.toString() : '0',
        AccountName: cardData.accountName || 'PYK Oturum',
        UserId: currentUser?.email || '',
        ScreenId: screenId || '',
      });
      setSnackbar({ open: true, message: 'Oturum başlatıldı', severity: 'success' });
      await loadSessionInfo(cardData.id);
    } catch (err: any) {
      console.error('[PYK] Oturum başlatma hatası:', err);
      setSnackbar({ open: true, message: err.response?.data?.message || 'Oturum başlatılamadı', severity: 'error' });
    }
    setSessionStartLoading(false);
  };

  const handleCloseSession = async () => {
    if (!activeSession) return;
    if (totalBalance > 0.005) {
      setSnackbar({ open: true, message: `Oturum kapatılamaz: Cüzdanda ${formatCurrency(totalBalance)} bakiye mevcut. Önce bakiyeyi iade ediniz.`, severity: 'warning' });
      return;
    }
    setSessionCloseLoading(true);
    try {
      await cardManagementService.closeSession({
        SessionId: activeSession.id,
        UserId: currentUser?.email || '',
      });
      setSnackbar({ open: true, message: 'Oturum kapatıldı', severity: 'success' });
      setActiveSession(null);
      setWalletBalances([]);
      setTransactions([]);
      focusCardSearch();
    } catch (err: any) {
      console.error('[PYK] Oturum kapatma hatası:', err);
      setSnackbar({ open: true, message: err.response?.data?.message || 'Oturum kapatılamadı', severity: 'error' });
    }
    setSessionCloseLoading(false);
  };
  const cardWallets = cardData?.wallets || [];

  const isPavopayTransaction = useCallback((tx: any): boolean => {
    if (!tx.fiscalTransactionData) return false;
    try {
      const fd = JSON.parse(tx.fiscalTransactionData);
      return !!(fd.saleNumber || fd.saleUid || (fd.payments && fd.payments.length > 0));
    } catch { return false; }
  }, []);

  const hasPendingPosAdvance = useCallback((): boolean => {
    if (!transactions || transactions.length === 0) return false;
    const pavopayPosLoads = transactions.filter((tx: any) =>
      tx.transactionType === 'LOAD' && tx.paymentMethod === 'POS' && isPavopayTransaction(tx)
    );
    if (pavopayPosLoads.length === 0) return false;
    const posWithdraws = transactions.filter((tx: any) => tx.transactionType === 'WITHDRAW' && tx.paymentMethod === 'POS');
    const totalPavopayLoaded = pavopayPosLoads.reduce((s: number, tx: any) => s + tx.amount, 0);
    const totalPosWithdrawn = posWithdraws.reduce((s: number, tx: any) => s + tx.amount, 0);
    const totalSpent = transactions.filter((tx: any) => tx.transactionType === 'PURCHASE').reduce((s: number, tx: any) => s + tx.amount, 0);
    const posRemaining = Math.max(0, totalPavopayLoaded - totalPosWithdrawn - totalSpent);
    return posRemaining > 0.005;
  }, [transactions, isPavopayTransaction]);

  const handleWithdrawClick = async () => {
    if (!activeSession || !cardData) return;

    // NOT: "Fişlenmemiş satış var, önce fişle" ön kontrolleri kaldırıldı.
    // Satışlar artık satış ANINDA gün sonuna + faturaya işlendiği için iade
    // doğrudan açılır.

    // ── Ingenico modu: iade HER ZAMAN nakit ve kasadan yapılır ──
    // Ingenico'da satış void/iade desteği yok (saleNumber dönmüyor) ve kullanıcı
    // talebi gereği iade nakit olacak. POS-avans dağıtımı yapılmaz.
    if (useIngenico) {
      const presetWallet = cardWallets.length === 1 ? cardWallets[0].walletId : '';
      setBalanceWalletId(presetWallet);
      setBalanceDescription('');
      if (presetWallet) {
        const wb = walletBalances.find((w: any) => w.walletId === presetWallet);
        const bal = wb?.balance || 0;
        setBalanceAmount(bal > 0 ? formatAmountInput(bal.toFixed(2).replace('.', ',')) : '');
      } else {
        setBalanceAmount('');
      }
      setWithdrawModalOpen(true);
      return;
    }

    const allTx = transactions;
    console.log('[İADE-ANALİZ] Tüm işlemler:', allTx.length, allTx.map((tx: any) => ({
      id: tx.id, type: tx.transactionType, method: tx.paymentMethod, amount: tx.amount,
      hasFiscalData: !!tx.fiscalTransactionData,
      fiscalDataPreview: tx.fiscalTransactionData ? tx.fiscalTransactionData.substring(0, 80) : null,
    })));
    const posLoads = allTx.filter((tx: any) => tx.transactionType === 'LOAD' && tx.paymentMethod === 'POS');
    console.log('[İADE-ANALİZ] POS yüklemeleri:', posLoads.length);

    const pavopayPosLoads = posLoads.filter((tx: any) => {
      const isPavo = isPavopayTransaction(tx);
      console.log('[İADE-ANALİZ] POS LOAD id:', tx.id, '→ PavoPay:', isPavo,
        tx.fiscalTransactionData ? '(fiscal data var)' : '(fiscal data yok)');
      return isPavo;
    });
    console.log('[İADE-ANALİZ] PavoPay POS yüklemeleri:', pavopayPosLoads.length);

    if (pavopayPosLoads.length === 0) {
      const presetWallet = cardWallets.length === 1 ? cardWallets[0].walletId : '';
      setBalanceWalletId(presetWallet);
      setBalanceDescription('');
      if (presetWallet) {
        const wb = walletBalances.find((w: any) => w.walletId === presetWallet);
        const bal = wb?.balance || 0;
        setBalanceAmount(bal > 0 ? formatAmountInput(bal.toFixed(2).replace('.', ',')) : '');
      } else {
        setBalanceAmount('');
      }
      setWithdrawModalOpen(true);
      return;
    }

    setRefundLoading(true);
    try {
      const posWithdraws = allTx.filter((tx: any) => tx.transactionType === 'WITHDRAW' && tx.paymentMethod === 'POS');
      const cashLoads = allTx.filter((tx: any) => tx.transactionType === 'LOAD' && tx.paymentMethod === 'CASH');
      const cashWithdraws = allTx.filter((tx: any) => tx.transactionType === 'WITHDRAW' && tx.paymentMethod === 'CASH');
      const purchases = allTx.filter((tx: any) => tx.transactionType === 'PURCHASE');
      const refunds = allTx.filter((tx: any) => tx.transactionType === 'REFUND');

      const nonPavopayPosLoads = posLoads.filter((tx: any) => !isPavopayTransaction(tx));
      const totalPavopayPosLoaded = pavopayPosLoads.reduce((s: number, tx: any) => s + tx.amount, 0);
      const totalNonPavopayPosLoaded = nonPavopayPosLoads.reduce((s: number, tx: any) => s + tx.amount, 0);
      const totalPosWithdrawn = posWithdraws.reduce((s: number, tx: any) => s + tx.amount, 0);
      const totalCashLoaded = cashLoads.reduce((s: number, tx: any) => s + tx.amount, 0);
      const totalCashWithdrawn = cashWithdraws.reduce((s: number, tx: any) => s + tx.amount, 0);
      const totalSpent = purchases.reduce((s: number, tx: any) => s + tx.amount, 0);
      const totalRefunded = refunds.reduce((s: number, tx: any) => s + tx.amount, 0);

      const netPosLoaded = Math.max(0, totalPavopayPosLoaded - totalPosWithdrawn);
      // PavoPay olmayan POS yüklemeleri Pavo cihazından iade edilemediği için
      // pratikte nakit gibi davranır (manuel iade kasadan yapılır).
      const netCashLoaded = Math.max(0, totalCashLoaded + totalNonPavopayPosLoaded - totalCashWithdrawn);
      const netSpent = Math.max(0, totalSpent - totalRefunded);

      // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
      // İADE DAĞITIMI - "POS önce harcanır" mantığı
      // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
      // Kullanıcı şikayeti (2026-05-22):
      //   "100 TL POS + 50 TL nakit yüklendi, 80 TL harcandı. Sistem POS iade
      //    butonunda 70 TL'yi POS'a gönderiyor. Olması gereken: 50 TL nakit
      //    iade + 20 TL POS iade. Nakit yüklemeler kesinlikle POS'a gitmemeli."
      //
      // Mantık: Harcamalar önce POS yüklemelerinden eksiltilir; nakit
      // yüklemelerin harcanmamış kısmı her zaman nakit olarak iade edilir.
      // Bu sayede:
      //   - Nakit yüklenen para Pavo cihazına gönderilmez (mali olarak yanlış).
      //   - POS yüklemelerinin harcanmamış kısmı Pavo'dan iade alınır.
      //
      // Örnekler:
      //   100 POS + 50 nakit, 0 harcama, 150 bakiye   → POS=100, Nakit=50
      //   100 POS + 50 nakit, 80 harcama, 70 bakiye   → POS=20,  Nakit=50  ✓
      //   100 POS + 50 nakit, 120 harcama, 30 bakiye  → POS=0,   Nakit=30
      //   100 POS + 50 nakit, 30 harcama, 120 bakiye  → POS=70,  Nakit=50
      const posSpent = Math.min(netSpent, netPosLoaded);
      const cashSpent = Math.max(0, netSpent - posSpent);
      const posRemaining = Math.max(0, netPosLoaded - posSpent);
      const cashRemaining = Math.max(0, netCashLoaded - cashSpent);

      const posRefundNeeded = Math.round(posRemaining * 100) / 100;
      const cashRefund = Math.round(cashRemaining * 100) / 100;

      setRefundBreakdown({
        posLoaded: Math.round(netPosLoaded * 100) / 100,
        cashLoaded: Math.round(netCashLoaded * 100) / 100,
        totalSpent: Math.round(netSpent * 100) / 100,
        posSpent: Math.round(posSpent * 100) / 100,
        cashSpent: Math.round(cashSpent * 100) / 100,
        posRemaining: Math.round(posRemaining * 100) / 100,
        cashRemaining: Math.round(cashRemaining * 100) / 100,
      });

      if (posRefundNeeded <= 0.005) {
        const presetWallet = cardWallets.length === 1 ? cardWallets[0].walletId : '';
        setBalanceWalletId(presetWallet);
        setBalanceDescription('');
        if (presetWallet) {
          const wb = walletBalances.find((w: any) => w.walletId === presetWallet);
          const bal = wb?.balance || 0;
          setBalanceAmount(bal > 0 ? formatAmountInput(bal.toFixed(2).replace('.', ',')) : '');
        } else {
          setBalanceAmount('');
        }
        setWithdrawModalOpen(true);
        setRefundLoading(false);
        return;
      }

      const unresolvedPosLoads = pavopayPosLoads.filter((tx: any) => {
        const txFiscal = JSON.parse(tx.fiscalTransactionData);
        const txIdentifier = txFiscal.saleId || txFiscal.saleNumber || txFiscal.saleUid;
        const matchingWithdraws = posWithdraws.filter((w: any) => {
          if (!w.fiscalTransactionData) return false;
          try {
            const wData = JSON.parse(w.fiscalTransactionData);
            return wData.relatedSaleId === txFiscal.saleId
              || wData.relatedSaleNumber === txFiscal.saleNumber
              || wData.relatedSaleId === txIdentifier;
          } catch { return false; }
        });
        const alreadyRefunded = matchingWithdraws.reduce((s: number, w: any) => s + w.amount, 0);
        return tx.amount - alreadyRefunded > 0.005;
      });

      if (unresolvedPosLoads.length === 0) {
        const presetWallet = cardWallets.length === 1 ? cardWallets[0].walletId : '';
        setBalanceWalletId(presetWallet);
        setBalanceDescription('');
        setBalanceAmount(totalBalance > 0 ? formatAmountInput(totalBalance.toFixed(2).replace('.', ',')) : '');
        setWithdrawModalOpen(true);
        setRefundLoading(false);
        return;
      }

      setPosLoadTransactions(unresolvedPosLoads);

      // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
      // 1) DAĞITIMI ÖNCE LOKAL OLARAK YAP (Pavo cihazına gitmeden)
      // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
      // Kullanıcı şikayeti (2026-05-22): "İade butonuna basınca cihaz GTS ↔
      // PavoPay arasında 6 kez gidip geliyor, baya bi yapıyor". Sebep: her
      // POS yüklemesi için ayrı `getReturnableSale` intent'i gönderiliyordu;
      // her intent PavoPay Activity'sini foreground'a getirip GTS'ye geri
      // dönüyor. Çözüm: dağıtımı `fiscalTransactionData` ile yerel olarak
      // hesapla, sadece dağıtıma giren (genelde 1-2) yüklemeler için
      // `getReturnableSale` çağır. Cihaz `_sendAndWait` pending Completer
      // paterni paralel intent'leri desteklemediği için seri çağrılır,
      // ama 6 yerine 1-2 sorgu olur.
      let remaining = posRefundNeeded;
      const dist: typeof refundDistribution = [];
      for (const tx of unresolvedPosLoads) {
        if (remaining <= 0.005) break;
        const fiscalData = JSON.parse(tx.fiscalTransactionData);
        const saleId = fiscalData.saleId || 0;
        const saleNumber = fiscalData.saleNumber || '';
        const saleUid = fiscalData.saleUid || '';
        // fiscalData zaten Pavo'nun ilk satış cevabını içerir → paymentId ve
        // mediatorId genelde burada bulunur. getReturnableSale'den dönen
        // güncel değerlerle aşağıda override edebiliriz.
        // ✅ EŞLENİKLİ İADE: Ödeme id'si (RelatedPaymentId) hem Direct REST
        // (küçük harf: id/mediatorId) hem REST-P2P (ham Pavo: Id/PaymentMediatorId)
        // formatından okunmalı. Aksi halde REST-P2P'de paymentId=0 kalıp iade
        // sessizce eşleniksize düşüyordu.
        const p0 = fiscalData.payments?.[0];
        const paymentId = p0?.id ?? p0?.Id ?? 0;
        const mediatorId = p0?.mediatorId ?? p0?.PaymentMediatorId ?? 2;

        const txIdentifier = saleId || saleNumber || saleUid;
        const posWithdrawsForThis = posWithdraws.filter((w: any) => {
          try {
            const d = JSON.parse(w.fiscalTransactionData);
            return d.relatedSaleId === txIdentifier
              || d.relatedSaleNumber === saleNumber
              || d.relatedSaleId === saleId;
          } catch { return false; }
        });
        const alreadyRefundedForThis = posWithdrawsForThis.reduce((s: number, w: any) => s + w.amount, 0);
        const maxRefundable = Math.round((tx.amount - alreadyRefundedForThis) * 100) / 100;
        const refundForThis = Math.min(remaining, maxRefundable);
        const isFullRefund = Math.abs(refundForThis - maxRefundable) < 0.005;

        dist.push({
          txId: tx.id,
          saleId,
          saleNumber,
          saleUid,
          paymentId,
          originalAmount: tx.amount,
          refundAmount: Math.round(refundForThis * 100) / 100,
          refundType: isFullRefund ? 'full' : 'partial',
          mediatorId,
        });
        remaining -= refundForThis;
      }

      // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
      // 2) SADECE EKSİK BİLGİSİ OLAN YÜKLEMELER İÇİN getReturnableSale
      // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
      // fiscalTransactionData zaten ilk satış cevabını (saleId, saleNumber,
      // payments[0].id, payments[0].mediatorId) içerir. Hepsi varsa
      // getReturnableSale tamamen atlanır → Pavo cihazı hiç tetiklenmez,
      // modal anında açılır. Eksik bilgili tx için seri çağrı yapılır
      // (cihaz tarafı paralel intent kabul etmiyor).
      const returnableMap: Record<number, any> = {};
      const needsQuery = dist.filter(item =>
        (!item.paymentId || !item.mediatorId)
        && (item.saleId || item.saleNumber || item.saleUid)
      );
      if (needsQuery.length === 0) {
        console.log('[İADE] getReturnableSale ATLANDI (fiscalData yeterli, dağıtım:', dist.length, 'kalem)');
      }
      for (const item of needsQuery) {
        try {
          console.log('[İADE] GetReturnableSale çağrılıyor (eksik payment bilgisi):', {
            saleId: item.saleId, saleNumber: item.saleNumber, saleUid: item.saleUid,
          });
          const retResult = await getReturnableSale(item.saleId, item.saleNumber, item.saleUid);
          if (retResult.success) {
            returnableMap[item.txId] = retResult;
            const payment = retResult.payments?.[0];
            if (payment) {
              if (payment.id) item.paymentId = payment.id;
              if (payment.mediatorId) item.mediatorId = payment.mediatorId;
            }
            if (retResult.saleId) item.saleId = retResult.saleId;
            if (retResult.saleNumber) item.saleNumber = retResult.saleNumber;
          } else {
            console.warn('[İADE] GetReturnableSale başarısız:', retResult.errorMessage);
            returnableMap[item.txId] = { success: false, errorMessage: retResult.errorMessage };
          }
        } catch (e) {
          console.error('[İADE] GetReturnableSale hatası:', e);
        }
      }
      setReturnableSaleDataMap(returnableMap);

      setRefundDistribution(dist);
      setPosRefundTotal(Math.round(posRefundNeeded * 100) / 100);
      setCashRefundAmount(Math.max(0, Math.round(cashRefund * 100) / 100));
      setRefundModalOpen(true);
    } catch (err: any) {
      console.error('[İADE] Analiz hatası:', err);
      setSnackbar({ open: true, message: 'İade analizi yapılırken hata oluştu', severity: 'error' });
    }
    setRefundLoading(false);
  };

  const handleProcessRefund = async (refundMethod: 'pos' | 'cash') => {
    if (!cardData || !activeSession) return;
    setRefundProcessing(true);
    const refundTotal = refundDistribution.reduce((s, it) => s + (it.refundAmount > 0 ? it.refundAmount : 0), 0);
    openWaitingModal('refund', refundMethod === 'cash' ? 'cash' : 'pos', refundTotal);
    if (!useRestP2P) {
      setPaymentWaitingMessage(refundMethod === 'cash' ? 'Nakit iade işlemi başlatılıyor...' : 'POS iade işlemi başlatılıyor...');
    } else {
      setRestP2PMessage(refundMethod === 'cash' ? 'Nakit iade işlemi başlatılıyor...' : 'POS iade işlemi başlatılıyor...');
    }

    const walletId = cardWallets.length === 1 ? cardWallets[0].walletId : (balanceWalletId || cardWallets[0]?.walletId);
    let allSuccess = true;

    for (let i = 0; i < refundDistribution.length; i++) {
      const item = refundDistribution[i];
      if (item.refundAmount <= 0) continue;

      const methodLabel = refundMethod === 'cash' ? 'Nakit' : 'POS';
      const stepMsg = `${methodLabel} İade ${i + 1}/${refundDistribution.length}: ${formatCurrency(item.refundAmount)} işleniyor...`;
      if (useRestP2P) setRestP2PMessage(stepMsg);
      else setPaymentWaitingMessage(stepMsg);

      const payments: PartialReturnPayment[] = [];
      if (refundMethod === 'cash') {
        payments.push({
          mediator: 1,
          amount: item.refundAmount,
          isVoid: false,
        });
      } else {
        // ✅ EŞLENİKLİ İADE GARANTİSİ: POS (void) iadesi orijinal ödemeye
        // referansla (RelatedPaymentId) yapılır. paymentId bir şekilde boş
        // kaldıysa (REST-P2P avans satış cevabı normalize edilmemiş ya da
        // getReturnableSale atlanmış olabilir), son bir kez cihazdan sorgulayıp
        // doldurmayı dene; yine de bulunamazsa kullanıcıyı uyar (sessizce
        // eşleniksize düşme).
        if (!item.paymentId && (item.saleId || item.saleNumber || item.saleUid)) {
          try {
            const ret = await getReturnableSale(item.saleId, item.saleNumber, item.saleUid);
            const pay = ret.payments?.[0];
            if (ret.success && pay?.id) {
              item.paymentId = pay.id;
              if (pay.mediatorId) item.mediatorId = pay.mediatorId;
            }
          } catch (e) {
            console.warn('[İADE] Eşlenikli iade için getReturnableSale son deneme hatası:', e);
          }
        }
        if (!item.paymentId) {
          console.warn('[İADE] ⚠️ Orijinal ödeme referansı (RelatedPaymentId) bulunamadı; bu kalem EŞLENİKSİZ iade edilecek.', { saleId: item.saleId, saleNumber: item.saleNumber });
          setSnackbar({ open: true, message: `Dikkat: ${item.saleNumber || item.saleId} için ödeme referansı bulunamadı, eşleniksiz iade yapılıyor.`, severity: 'warning' });
        }
        payments.push({
          mediator: item.mediatorId,
          amount: item.refundAmount,
          relatedPaymentId: item.paymentId || undefined,
          isVoid: true,
        });
      }

      try {
        const retData = returnableSaleDataMap[item.txId];
        const pavoResult = await processPartialReturnForAdvanceSale(item.saleId, payments, retData?.saleItems, item.saleNumber, item.saleUid);
        if (!pavoResult.success) {
          // Pavo cihazında "Vazgeç" basıldığında errorCode='CANCELLED_BY_USER' ve
          // cancelled=true geliyor. Bunu hata yerine "iptal" olarak gösteririz —
          // aksi halde kullanıcı iadeyi iptal etmiş olsa bile UI iade kaydını
          // başarılı sayıyor (orijinal şikayet).
          if (pavoResult.cancelled) {
            const cancelMsg = pavoResult.errorMessage || 'İade iptal edildi';
            setWaitingCancelled(cancelMsg);
            allSuccess = false;
            setSnackbar({ open: true, message: cancelMsg, severity: 'warning' });
          } else {
            setWaitingError(pavoResult.errorMessage || 'POS iade başarısız');
            allSuccess = false;
            setSnackbar({ open: true, message: `POS iade hatası: ${pavoResult.errorMessage}`, severity: 'error' });
          }
          break;
        }

        try {
          await cardManagementService.withdrawBalance({
            CardId: cardData.id,
            WalletId: walletId,
            Amount: item.refundAmount,
            Description: `${refundMethod === 'cash' ? 'Nakit' : 'POS'} avans iade - Satış: ${item.saleNumber || item.saleId}`,
            CreatedBy: currentUser?.email || '',
            ScreenId: screenId || '',
            PaymentMethod: refundMethod === 'cash' ? 'CASH' : 'POS',
            BankAccountId: refundMethod === 'cash' ? undefined : posOption?.bankAccountId,
            BankSubAccountId: refundMethod === 'cash' ? undefined : posOption?.subAccountId,
            FiscalSaleNumber: pavoResult.saleNumber,
            FiscalOrderNo: pavoResult.orderNo,
            FiscalInvoiceNo: pavoResult.invoiceNo,
            FiscalTransactionData: pavoResult.fiscalTransactionData,
            ReferenceId: pavoResult.saleNumber,
            ReferenceType: 'PAVOPAY_ADVANCE_RETURN',
          });
        } catch (beErr: any) {
          console.error('[İADE] Backend WITHDRAW kaydı hatası:', beErr);
          setSnackbar({ open: true, message: `POS iade PavoPay'de başarılı ancak kayıt hatası: ${beErr.message}`, severity: 'warning' });
        }
      } catch (err: any) {
        setWaitingError(err.message || 'POS iade bağlantı hatası');
        allSuccess = false;
        setSnackbar({ open: true, message: `POS iade hatası: ${err.message}`, severity: 'error' });
        break;
      }
    }

    if (allSuccess && cashRefundAmount > 0.005 && cashOption) {
      const cashMsg = 'Nakit iade işleniyor...';
      if (useRestP2P) setRestP2PMessage(cashMsg);
      else setPaymentWaitingMessage(cashMsg);
      try {
        const walletIdForCash = cardWallets.length === 1 ? cardWallets[0].walletId : (balanceWalletId || cardWallets[0]?.walletId);
        await cardManagementService.withdrawBalance({
          CardId: cardData.id,
          WalletId: walletIdForCash,
          Amount: cashRefundAmount,
          Description: 'Nakit bakiye iade',
          CreatedBy: currentUser?.email || '',
          ScreenId: screenId || '',
          PaymentMethod: 'CASH',
          CashboxId: cashOption.cashboxId,
        });
      } catch (cashErr: any) {
        console.error('[İADE] Nakit iade hatası:', cashErr);
        setSnackbar({ open: true, message: `Nakit iade hatası: ${cashErr.response?.data?.message || cashErr.message}`, severity: 'error' });
        allSuccess = false;
      }
    }

    if (allSuccess) {
      if (useRestP2P) {
        setRestP2PStatus('success');
        setRestP2PMessage('Tüm iadeler tamamlandı');
      } else {
        setPaymentWaitingStatus('success');
        setPaymentWaitingMessage('Tüm iadeler tamamlandı');
      }
      closeWaitingModal(1500);
      setRefundModalOpen(false);
      setSnackbar({ open: true, message: 'Bakiye iade işlemi tamamlandı', severity: 'success' });
    } else {
      closeWaitingModal(3000);
    }

    await loadSessionInfo(cardData.id);
    setRefundProcessing(false);
    focusCardSearch();
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#f4f6f8' }}>
      {/* Header */}
      <Box sx={{ bgcolor: '#1976d2', color: '#fff', px: 3, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CreditCardIcon />
          <Typography variant="h6" fontWeight={600}>{t('pykScreenView.title')}</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="body2" sx={{ opacity: 0.8 }}>{currentUser?.email}</Typography>
        </Box>
      </Box>

      <Container maxWidth="md" sx={{ flexGrow: 1, py: 3 }}>
        {/* Card Search */}
        <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>{t('pykScreenView.cardReading')}</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              fullWidth
              placeholder={t('pykScreenView.cardInputPlaceholder')}
              value={cardSearch}
              onChange={(e) => setCardSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') searchCard(); }}
              size="small"
              inputRef={cardSearchRef}
              autoFocus
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
            />
            {cardData ? (
              <Button variant="outlined" color="error" onClick={clearCard} sx={{ minWidth: 90 }}>
                {t('pykScreenView.close')}
              </Button>
            ) : (
              <Button variant="contained" onClick={searchCard} disabled={loading} sx={{ minWidth: 90 }}>
                {loading ? <CircularProgress size={20} /> : t('pykScreenView.scan')}
              </Button>
            )}
          </Box>
          {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
        </Paper>

        {cardData && (
          <>
            {/* Card Info & Session Status */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <Paper elevation={2} sx={{ p: 2, height: '100%' }}>
                  <Typography variant="subtitle2" fontWeight={600} color="text.secondary" gutterBottom>KART BİLGİLERİ</Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Typography variant="body2"><strong>Kart No:</strong> {cardData.cardNumber}</Typography>
                    {activeSession && <Typography variant="body2"><strong>Müşteri:</strong> {activeSession.accountName}</Typography>}
                  </Box>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper elevation={2} sx={{ p: 2, height: '100%' }}>
                  <Typography variant="subtitle2" fontWeight={600} color="text.secondary" gutterBottom>OTURUM DURUMU</Typography>
                  {activeSession ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Chip label="AKTİF" color="success" size="small" sx={{ alignSelf: 'flex-start' }} />
                      <Typography variant="body2"><strong>Başlangıç:</strong> {toLocalTime(activeSession.startedAt)}</Typography>
                    </Box>
                  ) : (
                    <Box>
                      <Chip label="OTURUM YOK" color="default" size="small" sx={{ mb: 1 }} />
                      <Typography variant="body2" color="text.secondary">Bu kartta aktif oturum bulunmuyor. Aşağıdaki "Oturum Başlat" butonu ile oturum açabilirsiniz.</Typography>
                    </Box>
                  )}
                </Paper>
              </Grid>
            </Grid>

            {/* Wallet Balances */}
            {activeSession && (
              <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
                <Typography variant="subtitle2" fontWeight={600} color="text.secondary" gutterBottom>CÜZDAN BAKİYELERİ</Typography>
                {walletBalances.length > 0 ? (
                  walletBalances.map((w: any) => (
                    <Box key={w.walletId} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                      <Typography variant="body2">{w.walletName}</Typography>
                      <Typography variant="body2" fontWeight={600}>{formatCurrency(w.balance)}</Typography>
                    </Box>
                  ))
                ) : (
                  cardWallets.map((w: any) => (
                    <Box key={w.walletId} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                      <Typography variant="body2">{w.walletName}</Typography>
                      <Typography variant="body2" fontWeight={600}>0,00 ₺</Typography>
                    </Box>
                  ))
                )}
                <Divider sx={{ my: 1.5 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
                  <Typography variant="h6" fontWeight={700}>TOPLAM</Typography>
                  <Typography variant="h5" fontWeight={700} color="primary">{formatCurrency(totalBalance)}</Typography>
                </Box>
              </Paper>
            )}

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
              {!activeSession ? (
                <>
                  {showSessionButtons && (
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={sessionStartLoading ? <CircularProgress size={20} color="inherit" /> : <PlayArrowIcon />}
                      size="large"
                      onClick={handleStartSession}
                      disabled={sessionStartLoading}
                      sx={{ minWidth: 200, py: 1.5 }}
                    >
                      {sessionStartLoading ? 'Başlatılıyor...' : 'Oturum Başlat'}
                    </Button>
                  )}
                  <Button variant="outlined" startIcon={<HistoryIcon />} onClick={loadHistory} sx={{ minWidth: 140 }}>
                    Geçmiş
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="contained" color="success" startIcon={<AddIcon />} size="large"
                    onClick={() => {
                      setBalanceAmount('');
                      const presetWallet = cardWallets.length === 1 ? cardWallets[0].walletId : '';
                      setBalanceWalletId(presetWallet);
                      setBalanceDescription('');
                      setLoadModalOpen(true);
                    }}
                    sx={{ minWidth: 160, py: 1.5 }}>
                    Bakiye Yükle
                  </Button>
                  <Button variant="contained" color="warning" startIcon={<RemoveIcon />} size="large"
                    onClick={handleWithdrawClick}
                    disabled={refundLoading}
                    sx={{ minWidth: 160, py: 1.5 }}>
                    {refundLoading ? <CircularProgress size={20} /> : 'Bakiye İade'}
                  </Button>
                  <Button variant="outlined" startIcon={<HistoryIcon />} onClick={loadHistory} sx={{ minWidth: 140 }}>
                    Geçmiş
                  </Button>
                  {useIngenico && (
                    <Button variant="outlined" color="warning" startIcon={<ReceiptLongIcon />}
                      onClick={() => checkIngenicoPending(true)}
                      disabled={ingenicoPendingChecking}
                      sx={{ minWidth: 160 }}>
                      {ingenicoPendingChecking ? 'Kontrol ediliyor...' : 'Yarım İşlem Kontrol'}
                    </Button>
                  )}
                  {showSessionButtons && (
                    <Button
                      variant="contained"
                      color="error"
                      startIcon={sessionCloseLoading ? <CircularProgress size={20} color="inherit" /> : <StopIcon />}
                      size="large"
                      onClick={handleCloseSession}
                      disabled={sessionCloseLoading}
                      sx={{ minWidth: 180, py: 1.5 }}
                    >
                      {sessionCloseLoading ? t('pykScreenView.closing') : t('pykScreenView.closeSession')}
                    </Button>
                  )}
                </>
              )}
            </Box>

            {/* Recent Transactions */}
            {activeSession && transactions.length > 0 && (
              <Paper elevation={2} sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2" fontWeight={600} color="text.secondary">SON İŞLEMLER</Typography>
                  {transactions.some((tx: any) => tx.transactionType === 'PURCHASE') && (
                    <Button
                      size="small"
                      startIcon={<ShoppingCartIcon />}
                      onClick={() => {
                        setPurchaseItemsMode('session');
                        setPurchaseReferenceId(null);
                        setPurchaseItemsOpen(true);
                      }}
                    >
                      Tüm Satın Alınan Ürünler
                    </Button>
                  )}
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Zaman</TableCell>
                        <TableCell>Tip</TableCell>
                        <TableCell>Ödeme</TableCell>
                        <TableCell>Cüzdan</TableCell>
                        <TableCell align="right">Tutar</TableCell>
                        <TableCell align="right">Bakiye</TableCell>
                        <TableCell align="center">İşlemler</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {transactions.map((tx: any) => (
                        <TableRow key={tx.id}>
                          <TableCell sx={{ fontSize: '0.8rem' }}>{toLocalTime(tx.createdAt)}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Chip size="small"
                                label={tx.transactionType === 'LOAD' ? 'Yükleme' : tx.transactionType === 'WITHDRAW' ? 'İade' : tx.transactionType === 'PURCHASE' ? 'Satın Alma' : tx.transactionType}
                                color={tx.transactionType === 'LOAD' || tx.transactionType === 'REFUND' ? 'success' : 'error'}
                              />
                              {tx.transactionType === 'PURCHASE' && tx.isReceiptPrinted && (
                                <Chip size="small" label="Fişlendi" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontSize: '0.65rem', height: 20 }} />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            {tx.paymentMethod === 'CASH' ? (
                              <Chip size="small" label="Nakit" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32' }} />
                            ) : tx.paymentMethod === 'POS' ? (
                              <Chip size="small" label="POS" sx={{ bgcolor: '#e3f2fd', color: '#1565c0' }} />
                            ) : tx.transactionType === 'PURCHASE' ? (
                              <Chip size="small" label="Kart" sx={{ bgcolor: '#fff3e0', color: '#e65100' }} />
                            ) : (
                              <Typography variant="caption" color="text.secondary">-</Typography>
                            )}
                          </TableCell>
                          <TableCell>{tx.walletName || '-'}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, color: (tx.transactionType === 'LOAD' || tx.transactionType === 'REFUND') ? 'green' : 'red' }}>
                            {(tx.transactionType === 'LOAD' || tx.transactionType === 'REFUND') ? '+' : '-'}{formatCurrency(tx.amount)}
                          </TableCell>
                          <TableCell align="right">{formatCurrency(tx.balanceAfter)}</TableCell>
                          <TableCell align="center">
                            {tx.transactionType === 'PURCHASE' ? (
                              <Tooltip title="Fiş Görüntüle">
                                <IconButton
                                  size="small"
                                  color="secondary"
                                  onClick={() => {
                                    setPurchaseItemsMode('transaction');
                                    setPurchaseReferenceId(tx.referenceId || tx.id.toString());
                                    setPurchaseItemsOpen(true);
                                  }}
                                >
                                  <ReceiptLongIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            ) : (
                              <Typography variant="caption" color="text.secondary">-</Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}
          </>
        )}
      </Container>

      {/* Load Balance Modal */}
      <Dialog open={loadModalOpen} onClose={() => { setLoadModalOpen(false); focusCardSearch(); }} maxWidth="xs" fullWidth
        TransitionProps={{ onEntered: () => setTimeout(() => amountInputRef.current?.focus(), 50) }}>
        <DialogTitle>Bakiye Yükle</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl size="small" required>
              <InputLabel>Cüzdan</InputLabel>
              <Select value={balanceWalletId} onChange={(e) => setBalanceWalletId(e.target.value as number)} label="Cüzdan">
                {cardWallets.map((w: any) => (
                  <MenuItem key={w.walletId} value={w.walletId}>{w.walletName}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Tutar (₺)"
              value={balanceAmount}
              onChange={(e) => setBalanceAmount(formatAmountInput(e.target.value))}
              size="small"
              placeholder="0,00"
              inputRef={amountInputRef}
              InputProps={{ endAdornment: <InputAdornment position="end">₺</InputAdornment> }}
            />
            <TextField label="Açıklama" value={balanceDescription} onChange={(e) => setBalanceDescription(e.target.value)} size="small" />
            {!cashOption && !posOption && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                Ödeme yöntemi tanımlanmamış. Lütfen PYK ekran ayarlarından nakit kasası veya POS banka hesabı tanımlayın.
              </Alert>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
              {cashOption && (
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => handleBalanceOperation('LOAD', 'CASH')}
                  disabled={balanceLoading || !balanceWalletId || !balanceAmount}
                  sx={{ py: 2, fontSize: '1.1rem', fontWeight: 'bold', bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' } }}
                >
                  {balanceLoading ? 'İşleniyor...' : '💵 NAKİT'}
                </Button>
              )}
              {posOption && (
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => handleBalanceOperation('LOAD', 'POS')}
                  disabled={balanceLoading || !balanceWalletId || !balanceAmount}
                  sx={{ py: 2, fontSize: '1.1rem', fontWeight: 'bold', bgcolor: '#1565c0', '&:hover': { bgcolor: '#0d47a1' } }}
                >
                  {balanceLoading ? 'İşleniyor...' : '💳 POS'}
                </Button>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setLoadModalOpen(false); focusCardSearch(); }}>İptal</Button>
        </DialogActions>
      </Dialog>

      {/* Withdraw Balance Modal */}
      <Dialog open={withdrawModalOpen} onClose={() => { setWithdrawModalOpen(false); focusCardSearch(); }} maxWidth="xs" fullWidth
        TransitionProps={{ onEntered: () => setTimeout(() => withdrawAmountRef.current?.focus(), 50) }}>
        <DialogTitle>Bakiye İade</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl size="small" required>
              <InputLabel>Cüzdan</InputLabel>
              <Select value={balanceWalletId} onChange={(e) => {
                const wId = e.target.value as number;
                setBalanceWalletId(wId);
                const wb = walletBalances.find((w: any) => w.walletId === wId);
                const bal = wb?.balance || 0;
                setBalanceAmount(bal > 0 ? formatAmountInput(bal.toFixed(2).replace('.', ',')) : '');
              }} label="Cüzdan">
                {cardWallets.map((w: any) => {
                  const bal = walletBalances.find((wb: any) => wb.walletId === w.walletId);
                  return (
                    <MenuItem key={w.walletId} value={w.walletId}>
                      {w.walletName} ({formatCurrency(bal?.balance || 0)})
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
            <TextField
              label="Tutar (₺)"
              value={balanceAmount}
              onChange={(e) => setBalanceAmount(formatAmountInput(e.target.value))}
              size="small"
              placeholder="0,00"
              inputRef={withdrawAmountRef}
              InputProps={{ endAdornment: <InputAdornment position="end">₺</InputAdornment> }}
            />
            <TextField label="Açıklama" value={balanceDescription} onChange={(e) => setBalanceDescription(e.target.value)} size="small" />
            {!cashOption && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                Nakit kasası tanımlanmamış. Lütfen PYK ekran ayarlarından nakit kasası tanımlayın.
              </Alert>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
              {cashOption && (
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => handleBalanceOperation('WITHDRAW', 'CASH')}
                  disabled={balanceLoading || !balanceWalletId || !balanceAmount}
                  sx={{ py: 2, fontSize: '1.1rem', fontWeight: 'bold', bgcolor: '#e65100', '&:hover': { bgcolor: '#bf360c' } }}
                >
                  {balanceLoading ? 'İşleniyor...' : '💵 NAKİT İADE'}
                </Button>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setWithdrawModalOpen(false); focusCardSearch(); }}>İptal</Button>
        </DialogActions>
      </Dialog>

      {/* Session History Modal */}
      <Dialog open={historyModalOpen} onClose={() => { setHistoryModalOpen(false); focusCardSearch(); }} maxWidth="md" fullWidth>
        <DialogTitle>Oturum Geçmişi</DialogTitle>
        <DialogContent>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Müşteri</TableCell>
                  <TableCell>Durum</TableCell>
                  <TableCell>Başlangıç</TableCell>
                  <TableCell>Bitiş</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sessionHistory.map((s: any) => (
                  <TableRow key={s.id}>
                    <TableCell>{s.accountName}</TableCell>
                    <TableCell><Chip label={s.status} size="small" color={s.status === 'ACTIVE' ? 'success' : 'default'} /></TableCell>
                    <TableCell>{toLocalTime(s.startedAt)}</TableCell>
                    <TableCell>{s.endedAt ? toLocalTime(s.endedAt) : '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setHistoryModalOpen(false); focusCardSearch(); }}>
            {t('pykScreenView.close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* POS Avans İade Dağılım Modalı */}
      <Dialog open={refundModalOpen} onClose={() => { if (!refundProcessing) { setRefundModalOpen(false); focusCardSearch(); } }} maxWidth="md" fullWidth>
        <DialogTitle>Bakiye İade - POS Avans Dağılımı</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mt: 1, mb: 2 }}>
            Toplam iade tutarı: <strong>{formatCurrency(posRefundTotal + cashRefundAmount)}</strong>
          </Alert>

          {refundBreakdown && (
            <Alert severity="info" icon={false} sx={{ mb: 2 }}>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                Dağıtım Hesabı
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Karta yüklenen <strong>nakit</strong> tutarı kesinlikle POS&apos;a gönderilmez —
                önce POS yüklemelerinden harcanır, harcanmamış nakit kısmı kasadan nakit iade
                edilir, POS yüklemelerinin kalan kısmı Pavo cihazından POS iadesi olarak işlenir.
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ bgcolor: 'transparent' }}>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell>POS Yüklenen (net)</TableCell>
                      <TableCell align="right"><strong>{formatCurrency(refundBreakdown.posLoaded)}</strong></TableCell>
                      <TableCell>Nakit Yüklenen (net)</TableCell>
                      <TableCell align="right"><strong>{formatCurrency(refundBreakdown.cashLoaded)}</strong></TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Harcanan (toplam)</TableCell>
                      <TableCell align="right">{formatCurrency(refundBreakdown.totalSpent)}</TableCell>
                      <TableCell colSpan={2} sx={{ color: 'text.secondary', fontSize: '0.78rem' }}>
                        Önce POS&apos;tan {formatCurrency(refundBreakdown.posSpent)}, sonra nakitten {formatCurrency(refundBreakdown.cashSpent)}
                      </TableCell>
                    </TableRow>
                    <TableRow sx={{ bgcolor: 'rgba(46, 125, 50, 0.06)' }}>
                      <TableCell><strong>POS&apos;tan İade</strong></TableCell>
                      <TableCell align="right"><strong>{formatCurrency(refundBreakdown.posRemaining)}</strong></TableCell>
                      <TableCell><strong>Nakitten İade</strong></TableCell>
                      <TableCell align="right"><strong>{formatCurrency(refundBreakdown.cashRemaining)}</strong></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Alert>
          )}

          <Typography variant="subtitle2" fontWeight={600} gutterBottom>POS İade İşlemleri</Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Tarih</TableCell>
                  <TableCell>Yükleme Tutarı</TableCell>
                  <TableCell>Satış No</TableCell>
                  <TableCell>İade Tutarı</TableCell>
                  <TableCell>İade Türü</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {refundDistribution.map((item, idx) => {
                  const tx = posLoadTransactions.find((t: any) => t.id === item.txId);
                  return (
                    <TableRow key={item.txId}>
                      <TableCell>{tx ? toLocalTime(tx.createdAt) : '-'}</TableCell>
                      <TableCell>{formatCurrency(item.originalAmount)}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                        {tx?.fiscalSaleNumber || '-'}
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          value={item.refundAmount.toFixed(2).replace('.', ',')}
                          onChange={(e) => {
                            const val = parseAmountInput(e.target.value);
                            if (isNaN(val) || val < 0) return;
                            const maxAllowed = item.originalAmount;
                            const clamped = Math.min(val, maxAllowed);
                            setRefundDistribution(prev => {
                              const updated = [...prev];
                              updated[idx] = {
                                ...updated[idx],
                                refundAmount: Math.round(clamped * 100) / 100,
                                refundType: Math.abs(clamped - maxAllowed) < 0.005 ? 'full' : 'partial',
                              };
                              const newPosTotal = updated.reduce((s, d) => s + d.refundAmount, 0);
                              setPosRefundTotal(Math.round(newPosTotal * 100) / 100);
                              return updated;
                            });
                          }}
                          sx={{ width: 120 }}
                          InputProps={{ endAdornment: <InputAdornment position="end">₺</InputAdornment> }}
                          disabled={refundProcessing}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={item.refundType === 'full' ? 'Tam İade (Eşlenikli)' : 'Kısmi İade (Eşlenikli)'}
                          size="small"
                          color="success"
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
                <TableRow>
                  <TableCell colSpan={3} align="right"><strong>POS İade Toplamı:</strong></TableCell>
                  <TableCell><strong>{formatCurrency(posRefundTotal)}</strong></TableCell>
                  <TableCell />
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          {cashRefundAmount > 0.005 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>Nakit İade</Typography>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography>Nakit İade Tutarı: <strong>{formatCurrency(cashRefundAmount)}</strong></Typography>
              </Paper>
            </Box>
          )}

          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body1">
              <strong>Özet:</strong> POS İade: {formatCurrency(posRefundTotal)}
              {cashRefundAmount > 0.005 && <> + Nakit İade: {formatCurrency(cashRefundAmount)}</>}
              {' '}= <strong>{formatCurrency(posRefundTotal + cashRefundAmount)}</strong>
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ gap: 1 }}>
          <Button onClick={() => { setRefundModalOpen(false); focusCardSearch(); }} disabled={refundProcessing}>İptal</Button>
          <Button
            variant="contained"
            color="success"
            onClick={() => handleProcessRefund('cash')}
            disabled={refundProcessing || posRefundTotal <= 0}
            startIcon={refundProcessing ? <CircularProgress size={18} /> : undefined}
          >
            {refundProcessing ? 'İşleniyor...' : 'Nakit İade'}
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => handleProcessRefund('pos')}
            disabled={refundProcessing || posRefundTotal <= 0}
            startIcon={refundProcessing ? <CircularProgress size={18} /> : undefined}
          >
            {refundProcessing ? 'İşleniyor...' : 'POS İade'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Hybrid bekleme modalı: Rest-P2P aktifken PavoRestP2PWaitModal, kapalıyken PaymentWaitingModal */}
      {!useRestP2P && (
        <PaymentWaitingModal
          open={paymentWaitingOpen}
          status={paymentWaitingStatus}
          message={paymentWaitingMessage}
          mode={paymentWaitingMode}
        />
      )}
      {useRestP2P && (
        <PavoRestP2PWaitModal
          open={restP2PModalOpen}
          status={restP2PStatus}
          paymentMode={restP2PMode}
          flowMode={restP2PFlowMode}
          totalAmount={restP2PTotal}
          parts={restP2PParts}
          message={restP2PMessage}
          deviceName={restP2PDeviceName}
        />
      )}

      <PurchaseItemsModal
        open={purchaseItemsOpen}
        onClose={() => setPurchaseItemsOpen(false)}
        companyId={companyId}
        mode={purchaseItemsMode}
        referenceId={purchaseReferenceId}
        sessionId={activeSession?.id || null}
      />

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>{snackbar.message}</Alert>
      </Snackbar>

      {/* Önceki timeout'tan cihazda asılı kalan yarım işlem → kurtarma */}
      <Dialog open={!!ingenicoPending} onClose={() => { if (!ingenicoPendingLoading) setIngenicoPending(null); }} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: 'warning.main', fontWeight: 700 }}>Cihazda Yarım Kalmış İşlem</DialogTitle>
        <DialogContent>
          {ingenicoPending && (() => {
            const paymentComplete = ingenicoPending.tx.remainingAmountTL <= 0.01;
            const ctx = ingenicoPending.ctx;
            const loadAmount = ctx?.amount ?? ingenicoPending.tx.totalAmountTL;
            return (
              <>
                <Typography sx={{ mb: 1.5 }}>
                  Yazarkasada tamamlanmamış (önceki zaman aşımından kalan) bir işlem bulundu.
                </Typography>
                <Box sx={{ bgcolor: 'action.hover', borderRadius: 1, p: 1.5, mb: 1.5 }}>
                  <Typography variant="body2">Fiş Tutarı: <b>{formatCurrency(ingenicoPending.tx.totalAmountTL)}</b></Typography>
                  <Typography variant="body2">Alınan Ödeme: <b>{formatCurrency(ingenicoPending.tx.totalPaymentTL)}</b></Typography>
                  <Typography variant="body2" sx={{ color: paymentComplete ? 'success.main' : 'error.main', fontWeight: 600 }}>
                    {paymentComplete ? '✓ Ödeme alınmış (fiş kapatılabilir)' : `Kalan: ${formatCurrency(ingenicoPending.tx.remainingAmountTL)} (ödeme tamamlanmamış)`}
                  </Typography>
                </Box>
                {ctx ? (
                  <Box sx={{ bgcolor: 'info.50', border: '1px solid', borderColor: 'info.light', borderRadius: 1, p: 1.5, mb: 1.5 }}>
                    <Typography variant="body2">Kart No: <b>{ctx.cardNumber || ctx.cardId}</b></Typography>
                    <Typography variant="body2">Yüklenecek Tutar: <b>{formatCurrency(loadAmount)}</b></Typography>
                  </Box>
                ) : (
                  paymentComplete && (
                    <Typography variant="body2" sx={{ color: 'warning.main', mb: 1.5 }}>
                      Bu işleme ait kart bağlamı bulunamadı. Ekranda <b>doğru kartın okutulu</b> olduğundan emin olun.
                    </Typography>
                  )
                )}
                {paymentComplete ? (
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Ödeme alındığı için fişi kapatıp bu tutarı ilgili karta yükleyebilir veya yalnızca fişi kapatabilirsiniz.
                  </Typography>
                ) : (
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Ödeme tamamlanmadığı için işlem iptal edilecektir. Bakiye yüklenmez.
                  </Typography>
                )}
              </>
            );
          })()}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1, flexWrap: 'wrap' }}>
          {ingenicoPending && ingenicoPending.tx.remainingAmountTL <= 0.01 ? (
            <>
              <Button onClick={() => handleIngenicoPendingRecover(false)} color="inherit" variant="outlined" disabled={ingenicoPendingLoading}>
                Sadece Fişi Kapat
              </Button>
              <Button onClick={() => handleIngenicoPendingRecover(true)} color="success" variant="contained" disabled={ingenicoPendingLoading}>
                {ingenicoPendingLoading ? 'İşleniyor...' : 'Fişi Kapat ve Bakiye Yükle'}
              </Button>
            </>
          ) : (
            <Button onClick={() => handleIngenicoPendingRecover(false)} color="error" variant="contained" disabled={ingenicoPendingLoading}>
              {ingenicoPendingLoading ? 'İşleniyor...' : 'İşlemi İptal Et'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PykScreenView;
